import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Row,
} from "react-bootstrap";
import DataTable from "react-data-table-component";
import moment from "moment";
import { toast } from "react-toastify";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import SkinAssistantNav from "../../components/admin/SkinAssistantNav";
import AdminEmptyState from "../../components/admin/AdminEmptyState";
import DataTableSkeleton from "../../components/DataTableSkeleton";
import ConfirmModal from "../../components/ConfirmModal";
import AdminRowActions, { AdminActionButton } from "../../components/admin/AdminRowActions";
import { dataTableCustomStyles, dataTablePaginationOptions } from "../../components/admin/dataTableConfig";
import {
  API_NOT_DEPLOYED_MSG,
  LEAD_CHANNEL_OPTIONS,
  deleteSkinAssistantLead,
  downloadCSV,
  exportLeadsToCSV,
  getSkinAssistantLeads,
  isSkinAssistantApiNotDeployed,
  maskPhone,
  parseLeadsResponse,
} from "../../helper/skin_assistant_helper";
import { getAdminToken } from "../../helper/auth_helper";

function LeadsList() {
  const token = getAdminToken();

  const [loading, setLoading] = useState(true);
  const [apiUnavailable, setApiUnavailable] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [leads, setLeads] = useState([]);
  const [pagination, setPagination] = useState(null);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [channelFilter, setChannelFilter] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchLeads = useCallback(async () => {
    if (!token) {
      setLoadError("Not authorized");
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError("");
    setApiUnavailable(false);
    try {
      const params = { page, limit: perPage };
      if (debouncedSearch) params.search = debouncedSearch;
      if (channelFilter) params.channel = channelFilter;

      const res = await getSkinAssistantLeads(params, token);
      const parsed = parseLeadsResponse(res);
      if (parsed.error) {
        setLoadError(parsed.error);
        setLeads([]);
        return;
      }
      setLeads(parsed.leads);
      setPagination(parsed.pagination);
    } catch (error) {
      if (isSkinAssistantApiNotDeployed(error)) {
        setApiUnavailable(true);
        setLeads([]);
      } else {
        setLoadError(error?.message || "Failed to load leads");
      }
    } finally {
      setLoading(false);
    }
  }, [token, page, perPage, debouncedSearch, channelFilter]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleDeleteClick = (lead) => {
    setLeadToDelete(lead);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!leadToDelete?.id) return;
    setDeleteLoading(true);
    try {
      await deleteSkinAssistantLead(leadToDelete.id, token);
      toast.success("Lead deleted");
      setConfirmOpen(false);
      setLeadToDelete(null);
      fetchLeads();
    } catch (error) {
      if (isSkinAssistantApiNotDeployed(error)) {
        toast.error(API_NOT_DEPLOYED_MSG);
      } else {
        toast.error(error?.message || "Delete failed");
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleExportCsv = () => {
    const csv = exportLeadsToCSV(leads);
    if (!csv) {
      toast.warning("No leads to export on this page");
      return;
    }
    downloadCSV(csv, `skin_assistant_leads_${new Date().toISOString().split("T")[0]}.csv`);
    toast.success("CSV exported");
  };

  const columns = useMemo(
    () => [
      {
        name: "Name",
        selector: (row) => row.name,
        sortable: true,
        grow: 1,
      },
      {
        name: "Email",
        selector: (row) => row.email,
        grow: 1.2,
      },
      {
        name: "Phone",
        cell: (row) => maskPhone(row.phone),
        width: "130px",
      },
      {
        name: "Channel",
        selector: (row) => row.channel,
        width: "110px",
        cell: (row) => row.channel || "—",
      },
      {
        name: "Consent",
        width: "170px",
        cell: (row) =>
          row.consentAt ? moment(row.consentAt).format("DD MMM YYYY, hh:mm A") : "—",
      },
      {
        name: "Consent text ver.",
        selector: (row) => row.consentTextVersion,
        width: "120px",
        cell: (row) => row.consentTextVersion || "—",
      },
      {
        name: "Actions",
        width: "100px",
        cell: (row) => (
          <AdminRowActions>
            <AdminActionButton
              variant="danger"
              icon="ri-delete-bin-line"
              title="Delete lead (data erasure)"
              onClick={() => handleDeleteClick(row)}
            />
          </AdminRowActions>
        ),
      },
    ],
    []
  );

  const totalCount = pagination?.total ?? leads.length;

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-md-flex align-items-center justify-content-between mb-3">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">
                <Link to="/admin/dashboard">Dashboard</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Skin Assistant
              </li>
            </ol>
            <h4 className="main-title mb-0 admin-page-title">Skin Assistant — Leads</h4>
            <p className="text-secondary small mb-0 mt-1">
              Consented contacts from the quiz; delete for erasure requests
            </p>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <Button variant="success" size="sm" onClick={handleExportCsv} disabled={!leads.length}>
              Export CSV
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={fetchLeads} disabled={loading}>
              Refresh
            </Button>
          </div>
        </div>

        <SkinAssistantNav />

        {apiUnavailable && (
          <Alert variant="warning" className="mb-3">{API_NOT_DEPLOYED_MSG}</Alert>
        )}
        {loadError && !apiUnavailable && (
          <Alert variant="danger" className="mb-3">{loadError}</Alert>
        )}

        <Card>
          <Card.Body>
            <Row className="g-2 mb-3 align-items-end">
              <Col md={5}>
                <Form.Label className="small">Search name, email, or phone</Form.Label>
                <Form.Control
                  size="sm"
                  type="search"
                  value={search}
                  onChange={(e) => {
                    setPage(1);
                    setSearch(e.target.value);
                  }}
                  placeholder="Search…"
                />
              </Col>
              <Col md={3}>
                <Form.Label className="small">Channel</Form.Label>
                <Form.Select
                  size="sm"
                  value={channelFilter}
                  onChange={(e) => {
                    setPage(1);
                    setChannelFilter(e.target.value);
                  }}
                >
                  <option value="">All channels</option>
                  {LEAD_CHANNEL_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Form.Select>
              </Col>
            </Row>

            {loading ? (
              <DataTableSkeleton rows={6} />
            ) : apiUnavailable ? (
              <AdminEmptyState
                icon="ri-cloud-off-line"
                title="Leads API not available"
                description={API_NOT_DEPLOYED_MSG}
              />
            ) : leads.length === 0 ? (
              <AdminEmptyState
                title="No leads yet"
                description="Leads appear when shoppers opt in after the Skin Assistant quiz."
              />
            ) : (
              <DataTable
                columns={columns}
                data={leads}
                customStyles={dataTableCustomStyles}
                pagination
                paginationServer
                paginationTotalRows={totalCount}
                paginationPerPage={perPage}
                paginationDefaultPage={page}
                onChangePage={setPage}
                onChangeRowsPerPage={(newPerPage, newPage) => {
                  setPerPage(newPerPage);
                  setPage(newPage);
                }}
                paginationComponentOptions={dataTablePaginationOptions}
                highlightOnHover
                responsive
              />
            )}
          </Card.Body>
        </Card>
      </div>

      <ConfirmModal
        show={confirmOpen}
        onCloseClick={() => {
          if (!deleteLoading) {
            setConfirmOpen(false);
            setLeadToDelete(null);
          }
        }}
        onConfirm={confirmDelete}
        data={{
          actionType: "Delete",
          message: leadToDelete
            ? `Permanently delete ${leadToDelete.name || "this lead"}? For data erasure requests. This cannot be undone.`
            : "Permanently delete this lead?",
        }}
      />

      <Footer />
    </>
  );
}

export default LeadsList;
